import apiClient, { ApiResponse } from "./client";
import { config } from "../config";
import { BachelorDegree, bachelorDegreesApi } from "./bachelor-degrees";
import { MasterDegree, masterDegreesApi } from "./master-degrees";
import { LanguageCertificate, languageCertificatesApi } from "./language-certificates";
import { gdqpCertificatesApi } from "@/lib/api/gdqp-certificates";
import { Award, awardsApi } from "./awards";
import { Scholarship, scholarshipApi } from "./scholarship";

export type SearchObjectType = "bachelor" | "master" | "language" | "gdqp" | "award" | "scholarship" | "mssv";

// Unified search result interface
export interface SearchResult {
	id: string;
	type: any;
	studentName: string;
	cccd: string;
	studentId?: string;
	number: string; // Certificate/degree/award number
	title: string;
	issuedDate?: string;
	institution?: string;
	status?: any;
	signDate? : string;
	registrationNumber?: string;
	recognitionDecisionNumber?: string;
	// Type-specific data
	originalData: BachelorDegree | MasterDegree | LanguageCertificate | Award | Scholarship;
}

export interface PublicSearchRequest {
	query: string;
	searchBy: "name" | "cccd" | "studentId" | "number";
	type?: "all" | SearchObjectType;
	limit?: number;
	page?: number;
}

export interface PublicSearchResponse {
	results: SearchResult[];
	total: number;
	query: string;
	searchBy: string;
	type: string;
}

// Helper function to convert bachelor degree to search result
const bachelorToSearchResult = (degree: BachelorDegree) => ({
	...degree,
	type: "bachelor",
	id: degree.degreeNumber,
	number: degree.degreeNumber,
	title: `Bằng Đại Học ${degree.major}`,
	institution: "Trường Đại Học Văn Lang",
	originalData: degree,
});

// Helper function to convert master degree to search result
const masterToSearchResult = (degree: MasterDegree): SearchResult => ({
	...degree,
	type: "master",
	id: degree.degreeNumber,
	number: degree.degreeNumber,
	title: `Bằng Thạc sĩ ${degree.major}`,
	issuedDate: degree.graduationYear + "-12-31", // Assume graduation in December
	institution: "Trường Đại Học Văn Lang", // Default institution
	originalData: degree,
});

// Helper function to convert language-like certificate to search result
const gdqpCertToSearchResult = (cert: LanguageCertificate): SearchResult => ({
	...cert,
	type: "gdqp",
	id: cert.certificateNumber,
	studentId: cert.studentId,
	studentName: cert.fullName ?? cert.studentName,
	number: cert.certificateNumber,
	title: `Chứng Chỉ Giáo Dục Quốc Phòng`,
	issuedDate: cert.issueDate,
	institution: cert.examCenter,
	originalData: cert,
	signDate: cert.signedDate
});
const languageCertToSearchResult = (cert: LanguageCertificate): SearchResult => ({
	...cert,
	type: "language",
	id: cert.certificateNumber,
	studentId: cert.studentId,
	number: cert.certificateNumber,
	title: `Chứng Chỉ ${cert.language} ${cert.level}`,
	issuedDate: cert.issueDate,
	institution: cert.examCenter,
	originalData: cert,
});

// Helper function to convert award to search result
const awardToSearchResult = (award: Award): SearchResult => ({
	...award,
	type: "award",
	id: award.awardNumber,
	studentId: award.studentId,
	number: award.awardNumber,
	issuedDate: award.awardDate,
	institution: award.awardedBy,
	originalData: award,
});

const scholarshipToSearchResult = (scholarship: Scholarship): SearchResult => ({
	...scholarship,
	type: "scholarship",
	id: scholarship.certificateNo,
	title: scholarship.scholarshipName,
	number: scholarship.certificateNo,
	originalData: scholarship,
});

export const publicSearchApi = {
	// Universal search function
	search: async (request: PublicSearchRequest): Promise<ApiResponse<PublicSearchResponse>> => {
		try {
			const response = await apiClient.get<PublicSearchResponse>(`${config.endpoints.public.search}`, request);
			return response;
		} catch (error) {
			console.log(error);
			return {
				data: {
					results: [],
					total: 1,
					query: "",
					searchBy: "",
					type: "",
				},
				message: "",
				success: true,
				total: 0,
				page: 0,
				totalPages: 0,
			};
		}
	},

	// Get detailed information by ID and type
	getDetail: async ({ id, type }: { id: string, type: SearchObjectType, }): Promise<ApiResponse<SearchResult>> => {
		try {
			let result: SearchResult | null = null;
			let bachelor, master, gdqp;
			switch (type) {
				case "bachelor": {
					bachelor = await bachelorDegreesApi.getById(id);
					if (bachelor.success && bachelor.data) {
						result = bachelorToSearchResult(bachelor.data);
					}
					break;
				}

				case "master": {
					master = await masterDegreesApi.getById(id);
					if (master.success && master.data) {
						result = masterToSearchResult(master.data);
					}
					break;
				}

				case "language": {
					const languageCert = await languageCertificatesApi.getById(id);
					if (languageCert.success && languageCert.data) {
						result = languageCertToSearchResult(languageCert.data);
					}
					break;
				}

				case "gdqp": {
					const gdqpCert = await gdqpCertificatesApi.getById(id);
					if (gdqpCert.success && gdqpCert.data) {
						result = gdqpCertToSearchResult(gdqpCert.data);
					}
					break;
				}

				case "award": {
					const awardResult = await awardsApi.getById(id);
					if (awardResult.success && awardResult.data) {
						result = awardToSearchResult(awardResult.data);
					}
					break;
				}
				case "scholarship": {
					const scholarship = await scholarshipApi.getById(id);
					if (scholarship.success && scholarship.data) {
						result = scholarshipToSearchResult(scholarship.data);
					}
					break;
				}
				case "mssv": {
					try {
						bachelor = await bachelorDegreesApi.getById(id, "studentId", false);
						if (bachelor.success && bachelor.data) {
							result = bachelorToSearchResult(bachelor.data);
							break;
						}
					} catch (error) {
						console.log(error);

					}
					try {
						master = await masterDegreesApi.getById(id, "studentId", false);
						if (master.success && master.data) {
							result = masterToSearchResult(master.data);
							break;
						}
					} catch (error) {
						console.log(error);

					}
					try {
						gdqp = await gdqpCertificatesApi.getById(id, "studentId", false);
						if (gdqp.success && gdqp.data) {
							result = gdqpCertToSearchResult(gdqp.data);
							break;
						}
					} catch (error) {
						console.log(error);

					}
					// Bổ sung thêm của Tiến sĩ
					break;
				}
				default:
					throw new Error("Invalid type specified");
			}

			if (result) {
				return {
					success: true,
					data: result,
				};
			} else {
				throw new Error("Record not found");
			}
		} catch (error) {
			console.error("Error getting detail:", error);
			throw error;
		}
	},

	// Verify certificate/degree authenticity
	verify: async (number: string, type?: string): Promise<ApiResponse<{ valid: boolean; result?: SearchResult }>> => {
		try {
			const response = await apiClient.get<{ valid: boolean; result?: SearchResult }>(`${config.endpoints.public.search}/verify`, { number, type });
			return response;
		} catch (error) {
			console.error("Error verifying:", error);

			// Fallback verification using search
			const searchResult = await publicSearchApi.search({
				query: number,
				searchBy: "number",
				type: type as any,
				limit: 1,
			});

			if (searchResult.success && searchResult.data.results.length > 0) {
				const result = searchResult.data.results[0];
				return {
					success: true,
					data: {
						valid: result.status === "valid",
						result: result.status === "valid" ? result : undefined,
					},
				};
			}

			return {
				success: true,
				data: {
					valid: false,
				},
				message: "Không tìm thấy thông tin xác thực",
			};
		}
	},

	// Get statistics for public display
	getStats: async (): Promise<
		ApiResponse<{
			totalRecords: number;
			bachelorDegrees: number;
			masterDegrees: number;
			languageCertificates: number;
			awards: number;
			lastUpdated: string;
		}>
	> => {
		try {
			const response = await apiClient.get<{
				totalRecords: number;
				bachelorDegrees: number;
				masterDegrees: number;
				languageCertificates: number;
				awards: number;
				lastUpdated: string;
			}>(`${config.endpoints.public.search}/stats`);
			return response;
		} catch (error) {
			console.error("Error fetching stats:", error);

			// Return mock stats
			return {
				success: true,
				data: {
					totalRecords: 12750,
					bachelorDegrees: 8500,
					masterDegrees: 2800,
					languageCertificates: 1200,
					awards: 250,
					lastUpdated: new Date().toISOString(),
				},
			};
		}
	},
};
